import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useCancelBooking } from "../../../../../api/fleet-management/bookings/booking.hooks";
import {
  useUploadMediaMutation,
  useDeleteMediaMutation,
} from "../../../../../api/fleet-management/media/media.hooks";
import {
  extractFleetMediaFilename,
  getFleetUploadRelativeUrl,
} from "../../../../../api/fleet-management/api";

const getTodayDateValue = () => new Date().toISOString().slice(0, 10);

const initialCancelForm = {
  mode: "",
  amount: "",
  paymentDate: getTodayDateValue(),
  description: "",
  receipt: null,
  transactionId: "",
};

const CancelBookingModal = ({ show, onClose, bookingId, onSuccess }) => {
  const [form, setForm] = useState(initialCancelForm);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const { mutate: cancelBooking, isLoading: isSubmitting } = useCancelBooking();
  const { mutateAsync: uploadMedia, isLoading: isUploading } = useUploadMediaMutation();
  const { mutateAsync: deleteMedia, isLoading: isDeletingMedia } = useDeleteMediaMutation();

  useEffect(() => {
    if (!show) return;
    setForm({
      mode: "",
      amount: "",
      paymentDate: getTodayDateValue(),
      description: "",
      receipt: null,
      transactionId: "",
    });
    setErrors({});
    setFeedback({ type: "", message: "" });
  }, [show]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));

    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleReceiptUpload = async (file) => {
    if (!file) return;

    try {
      setErrors((prev) => ({ ...prev, receipt: undefined }));
      const uploadResult = await uploadMedia(file);
      const receiptUrl = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!receiptUrl) {
        throw new Error("Upload response did not include media path.");
      }

      setForm((prev) => ({
        ...prev,
        receipt: receiptUrl,
      }));
    } catch (uploadError) {
      setErrors((prev) => ({
        ...prev,
        receipt: uploadError?.response?.data?.message || "Failed to upload receipt.",
      }));
    }
  };

  const handleRemoveReceipt = async () => {
    if (!form.receipt) return;

    const filename = extractFleetMediaFilename(form.receipt);

    const removeFromState = () => {
      setForm((prev) => ({
        ...prev,
        receipt: null,
      }));
    };

    if (!filename) {
      removeFromState();
      return;
    }

    try {
      await deleteMedia({ filename });
      removeFromState();
    } catch {
      removeFromState();
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.mode) {
      nextErrors.mode = "Payment mode is required.";
    }

    if (form.amount === "" || Number(form.amount) < 0) {
      nextErrors.amount = "Amount is required.";
    }

    if (!form.description.trim()) {
      nextErrors.description = "Description is required.";
    }

    if (!form.paymentDate) {
      nextErrors.paymentDate = "Payment date is required.";
    }

    const needsTransactionId = form.mode === "online" || form.mode === "direct-bank-transfer";
    if (needsTransactionId && !form.transactionId.trim()) {
      nextErrors.transactionId = "Transaction ID is required for online and direct bank transfer.";
    }

    if (!form.receipt) {
      nextErrors.receipt = "Receipt is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!bookingId || !validateForm()) return;

    const payload = {
      paymentType: "refund",
      mode: form.mode,
      amount: Number(form.amount),
      paymentDate: form.paymentDate,
      description: form.description.trim(),
      receipt: form.receipt,
      transactionId: form.transactionId?.trim() || undefined,
      status: "completed",
    };

    cancelBooking(
      { bookingId, payload },
      {
        onSuccess: () => {
          setFeedback({ type: "success", message: "Booking cancelled successfully." });
          onSuccess?.();
          onClose?.();
        },
        onError: (cancelError) => {
          setFeedback({
            type: "danger",
            message: cancelError?.response?.data?.message || "Failed to cancel booking.",
          });
        },
      }
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Cancel Booking</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {feedback.message && (
          <Alert variant={feedback.type} className="mb-3">
            {feedback.message}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            Payment Type <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control type="text" value="refund" disabled />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Mode <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={form.mode}
            onChange={(e) => updateField("mode", e.target.value)}
            isInvalid={!!errors.mode}
            disabled={isSubmitting}
          >
            <option value="">Select payment mode</option>
            <option value="online">online</option>
            <option value="cash">cash</option>
            <option value="direct-bank-transfer">direct-bank-transfer</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">{errors.mode}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Amount <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="number"
            min="0"
            value={form.amount}
            onChange={(e) => updateField("amount", e.target.value)}
            isInvalid={!!errors.amount}
            disabled={isSubmitting}
          />
          <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Description <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            isInvalid={!!errors.description}
            disabled={isSubmitting}
          />
          <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Payment Date <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="date"
            value={form.paymentDate}
            onChange={(e) => updateField("paymentDate", e.target.value)}
            isInvalid={!!errors.paymentDate}
            disabled={isSubmitting}
          />
          <Form.Control.Feedback type="invalid">{errors.paymentDate}</Form.Control.Feedback>
        </Form.Group>

        {(form.mode === "online" || form.mode === "direct-bank-transfer") && (
          <Form.Group className="mb-3">
            <Form.Label>
              Transaction ID <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.transactionId}
              onChange={(e) => updateField("transactionId", e.target.value)}
              isInvalid={!!errors.transactionId}
              disabled={isSubmitting}
              placeholder="Enter transaction id"
            />
            <Form.Control.Feedback type="invalid">{errors.transactionId}</Form.Control.Feedback>
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>
            Receipt <span className="text-danger">*</span>
          </Form.Label>
          {form.receipt ? (
            <div className="d-flex align-items-center gap-2 mb-2">
              <small className="text-muted text-truncate flex-grow-1">{extractFleetMediaFilename(form.receipt)}</small>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveReceipt}
                disabled={isDeletingMedia || isSubmitting}
              >
                Remove
              </Button>
            </div>
          ) : (
            <Form.Control
              type="file"
              onChange={(e) => handleReceiptUpload(e.target.files?.[0])}
              isInvalid={!!errors.receipt}
              disabled={isSubmitting || isUploading}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
          )}
          {isUploading && (
            <small className="text-muted">
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Uploading receipt...
            </small>
          )}
          {!form.receipt && !isUploading && (
            <Form.Text className="text-muted">
              Supported formats: PDF, DOC, DOCX, JPG, PNG
            </Form.Text>
          )}
          <Form.Control.Feedback type="invalid">{errors.receipt}</Form.Control.Feedback>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={isSubmitting || isUploading || isDeletingMedia}>
          Close
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={isSubmitting || isUploading || isDeletingMedia || !form.receipt}>
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Cancelling...
            </>
          ) : (
            "Confirm Cancel"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelBookingModal;
